"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SingleDatePicker = void 0;
var classnames_1 = require("classnames");
var date_fns_1 = require("date-fns");
var PropTypes = require("prop-types");
var React = require("react");
var react_1 = require("react");
var ButtonAction_1 = require("../ButtonAction");
var Calendar_1 = require("../Calendar");
var ErrorMessage_1 = require("../ErrorMessage");
var HelpText_1 = require("../HelpText");
var Icon_1 = require("../Icon");
exports.SingleDatePicker = (0, react_1.forwardRef)(function (_a, ref) {
    var calendarChildren = _a.calendarChildren, _b = _a.className, className = _b === void 0 ? '' : _b, _c = _a.dateFormat, dateFormat = _c === void 0 ? 'dd/MM/yyyy' : _c, displayInline = _a.displayInline, displayOnIconClickOnly = _a.displayOnIconClickOnly, _d = _a.errormessage, errormessage = _d === void 0 ? '' : _d, helptext = _a.helptext, hideIcon = _a.hideIcon, id = _a.id, ignoreDates = _a.ignoreDates, ignoreWeekends = _a.ignoreWeekends, initialCalendarMonth = _a.initialCalendarMonth, _e = _a.isValidDate, isValidDate = _e === void 0 ? true : _e, label = _a.label, maximumDate = _a.maximumDate, minimumDate = _a.minimumDate, name = _a.name, onBlur = _a.onBlur, onChange = _a.onChange, onFocus = _a.onFocus, screenReaderText = _a.screenReaderText, _f = _a.value, value = _f === void 0 ? '' : _f;
    var modRef = (React.useRef());
    var calRef = (React.useRef());
    var now = new Date();
    var smallScreenSize = 768;
    var screenText = screenReaderText ? screenReaderText : 'Enter date using text field or continue navigating to use the date picker.';
    var initalStartMonth = value ? null : initialCalendarMonth;
    var _g = (0, react_1.useState)(value ? new Date(value) : null), selectedDate = _g[0], setSelectedDate = _g[1];
    var _h = (0, react_1.useState)(''), inputDate = _h[0], setInputDate = _h[1];
    var _j = (0, react_1.useState)(false), isInputSelectedByCalendar = _j[0], setIsInputSelectedByCalendar = _j[1];
    var _k = (0, react_1.useState)(initalStartMonth !== null && initalStartMonth !== void 0 ? initalStartMonth : now), calendarMonthYear = _k[0], setCalendarMonthYear = _k[1];
    var _l = (0, react_1.useState)('initialLoad'), calendarState = _l[0], setCalendarState = _l[1];
    var _m = (0, react_1.useState)(false), calendarFocus = _m[0], setCalendarFocus = _m[1];
    var _o = (0, react_1.useState)(null), focusDate = _o[0], setFocusDate = _o[1];
    var _p = (0, react_1.useState)(displayInline), showCalendar = _p[0], setShowCalendar = _p[1];
    var _q = (0, react_1.useState)(true), displayCalBottom = _q[0], setDisplayCalBottom = _q[1];
    /**
     * Input date checker
     * Function is used to check if a date is a valid date, in range, and shouldn't be ignored
     * Called in initial useEffect of setting start date, on input focus and on input blur
     */
    var isDateValid = function (checkDate) {
        var isValidStartDate = true;
        if (!(0, date_fns_1.isValid)(checkDate)) {
            /* istanbul ignore next */
            return false;
        }
        if (ignoreWeekends) {
            isValidStartDate = !(0, date_fns_1.isWeekend)(checkDate);
        }
        if (ignoreDates && ignoreDates.length >= 1) {
            isValidStartDate = isValidStartDate && !ignoreDates.includes((0, date_fns_1.format)(checkDate, dateFormat));
        }
        if (maximumDate) {
            isValidStartDate = isValidStartDate && (0, date_fns_1.isAfter)(maximumDate, checkDate);
        }
        if (minimumDate) {
            isValidStartDate = isValidStartDate && (0, date_fns_1.isBefore)(minimumDate, checkDate);
        }
        return isValidStartDate;
    };
    /**
     * Set initial start date
     * Watch for invalid start date and clear field if it is true
     */
    (0, react_1.useEffect)(function () {
        var current = new Date();
        setInputDate(value);
        var isValidStart = isValidDate;
        // set selected days on calendars
        if (isValidStart) {
            var parsedStart = (0, date_fns_1.parse)(value, dateFormat, current);
            if ((0, date_fns_1.isValid)(parsedStart)) {
                if (isDateValid(parsedStart)) {
                    setSelectedDate(parsedStart);
                    setCalendarMonthYear(parsedStart);
                    return;
                }
            }
        }
        setSelectedDate(null);
        setCalendarMonthYear(initialCalendarMonth !== null && initialCalendarMonth !== void 0 ? initialCalendarMonth : current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValidDate, dateFormat]);
    /**
     * Handle Calendar display position
     * If the calendar is not set to display inline check to see if there is space below the input field to display.
     * If this is not the case, check if there is space to display the calendar above the input field.
     * If this is not the case display below.
     */
    (0, react_1.useEffect)(function () {
        if (calendarFocus && modRef.current) {
            if (displayCalBottom) {
                if (calRef.current.getBoundingClientRect().bottom + window.scrollY > window.innerHeight) {
                    if (calRef.current.getBoundingClientRect().top > calRef.current.getBoundingClientRect().height) {
                        setDisplayCalBottom(false);
                        return;
                    }
                }
            }
        }
        setDisplayCalBottom(true);
        // eslint-disable-next-line
    }, [calendarFocus]);
    /**
     * Input field Focus handler
     * hide and show calendar if not display inline
     * set the calendar to the correct start date and value
     */
    var onInputFocus = function (event) {
        event.preventDefault();
        if (!displayInline && !displayOnIconClickOnly) {
            hideShowCalendar(false);
        }
        var inputString = event.target.value;
        if (event.target.name === name + "StartDate") {
            setIsInputSelectedByCalendar(false);
        }
        // if input field already has a value and it is valid, set the calendar month to that value.
        if (inputString) {
            var currentInputDate = (0, date_fns_1.parse)(inputString, dateFormat, now);
            var isValidStart = isValidDate && isDateValid(currentInputDate);
            if (isValidStart) {
                setCalendarMonthYear(currentInputDate);
                setFocusDate(currentInputDate);
            }
        }
        if (onFocus) {
            onFocus(event);
        }
    };
    /**
     * Input out of focus handler
     * validate the date
     * set the date in the calendar
     * activate the calendar if position absolute
     */
    var onInputBlur = function (event) {
        if (onBlur) {
            onBlur(event);
        }
        if (!inputDate.includes('/') && inputDate.length > 0) {
            var dateInFormat = inputDate.replace(/\D+/g, '');
            if (dateInFormat.length === 8) {
                setInputDate(dateInFormat.slice(0, 2) + "/" + dateInFormat.slice(2, 4) + "/" + dateInFormat.slice(4));
            }
        }
        var parsedDateStart = (0, date_fns_1.parse)(inputDate, dateFormat, now);
        var isStartValid = isValidDate && isDateValid(parsedDateStart);
        // activate calendar that should be in focus. handle when button is clicked multiple times and incorrect date
        if (!isStartValid) {
            if (initialCalendarMonth) {
                setCalendarState(calendarState === 'start-date' ? 'start-date' : 'start-date2');
                setCalendarMonthYear(initialCalendarMonth);
            }
            else {
                setCalendarState('start-date-invalid');
                setSelectedDate(null);
                setCalendarMonthYear(now);
            }
        }
        else {
            setCalendarState(calendarState === 'start-date' ? 'start-date' : 'start-date2');
            setSelectedDate(parsedDateStart);
            setCalendarMonthYear(parsedDateStart);
        }
    };
    var onInputChange = function (event) {
        var inputValue = event.target.value;
        setInputDate(inputValue);
        if (onChange) {
            onChange(name, inputValue);
        }
    };
    var onPreviousMonthBtnClick = function () {
        changeMonthDisplay('previous');
        // activate calendar that should be in focus. handle when button is clicked multiple times
        setCalendarState(calendarState === 'button-click' ? 'button-click2' : 'button-click');
    };
    var onNextMonthBtnClick = function () {
        changeMonthDisplay('next');
        // activate calendar that should be in focus. handle when button is clicked multiple times
        setCalendarState(calendarState === 'button-click' ? 'button-click2' : 'button-click');
    };
    /**
     * Calendar day click function
     * activated within the calendar.tsx this function sets the input field value, closes the calendar if absolute
     * and triggers the onchange event if passed through in component creation
     */
    var onCalendarDayClick = function (event) {
        var dataset = event.currentTarget.dataset;
        if (!dataset.day && !dataset.year && !dataset.month) {
            /* istanbul ignore next */
            return;
        }
        var currentSelectedDate = new Date(Number(dataset.year), Number(dataset.month), Number(dataset.day));
        if (selectedDate && (0, date_fns_1.isSameDay)(currentSelectedDate, selectedDate)) {
            return;
        }
        var formattedDate = (0, date_fns_1.format)(currentSelectedDate, dateFormat);
        setSelectedDate(currentSelectedDate);
        setInputDate(formattedDate);
        setIsInputSelectedByCalendar(true);
        if (onChange) {
            onChange(name + "StartDate", formattedDate);
        }
        if (!displayInline) {
            modRef.current.querySelector("#" + id + "-input").focus();
            setCalendarState(calendarState === 'start-date' ? 'initialLoad' : 'startDate');
            if (displayOnIconClickOnly) {
                hideShowCalendar(false, 1);
            }
        }
    };
    /**
     * on click event for calendar icon
     * used for when the calendar is not drawn inline
     */
    var onIconClick = function (event) {
        if (!displayInline) {
            hideShowCalendar(true);
        }
    };
    /**
     * move the correct calendar into display.
     * used by both the buttons in calendar view of previous and next and calendar days when arrowing left and right up and down
     */
    var changeMonthDisplay = function (direction) {
        if (direction === 'previous') {
            var prevStartMonthYear = (0, date_fns_1.subMonths)(calendarMonthYear, 1);
            // if the date is the same as the current date or out of range do nothing
            if (minimumDate && (0, date_fns_1.isBefore)(prevStartMonthYear, minimumDate) && !(0, date_fns_1.isSameDay)(prevStartMonthYear, minimumDate)) {
                return;
            }
            setCalendarMonthYear(prevStartMonthYear);
        }
        else {
            var nextStartMonthYear = (0, date_fns_1.addMonths)(calendarMonthYear, 1);
            // if the date is the same as the current date or out of range do nothing
            if (maximumDate && (0, date_fns_1.isAfter)(nextStartMonthYear, maximumDate)) {
                return;
            }
            setCalendarMonthYear(nextStartMonthYear);
        }
    };
    /**
     * Hide and show the calendar in absolute position
     * used to manage display and focus when the input, calendar icon and backdrop is clicked
     * if calendar or input icon is clicked, return focus to the input field
     */
    var hideShowCalendar = (0, react_1.useCallback)(function (returnFocus, hideCalendar) {
        if (returnFocus && !showCalendar && !calendarFocus) {
            modRef.current.querySelector("#" + id + "-input").focus();
        }
        if (hideCalendar === 1) {
            setShowCalendar(false);
            setCalendarFocus(false);
        }
        else {
            setShowCalendar(!showCalendar);
            setCalendarFocus(!calendarFocus);
        }
    }, [setShowCalendar, setCalendarFocus, calendarFocus, showCalendar, id, modRef]);
    var onOuterMouseClick = (0, react_1.useCallback)(function (evt) {
        if (!displayInline) {
            setTimeout(function () {
                if (!modRef.current.contains(evt.target)) {
                    hideShowCalendar(false, 1);
                }
            }, 5);
        }
    }, [displayInline, hideShowCalendar, modRef]);
    /**
     * Handle Calendar display when focusing outside of the component
     */
    (0, react_1.useEffect)(function () {
        if (showCalendar) {
            // add eventListener just when calendar shown
            document.body.addEventListener('click', onOuterMouseClick);
        }
        else {
            // remove eventListener when calendar hidden
            document.body.removeEventListener('click', onOuterMouseClick);
        }
        return function () {
            // remove eventListener when calendar removed from DOM
            document.body.removeEventListener('click', onOuterMouseClick);
        };
    }, [onOuterMouseClick, showCalendar]);
    /**
     * switch focus from one calendar to the next
     * used by calendar, changes the date of focus and the calendar state to activate change
     */
    var switchCalendarFocus = function (datefocus) {
        setFocusDate(datefocus);
        setCalendarState(calendarState === 'day-scroll' ? 'day-scroll2' : 'day-scroll');
    };
    var ErrorMessageComponent;
    var HelpTextComponent;
    var IconComponent;
    var ariaDiscribedBy;
    var ariaInValid = false;
    if (!isValidDate && !isInputSelectedByCalendar && errormessage) {
        ErrorMessageComponent = (React.createElement(ErrorMessage_1.ErrorMessage, { id: id, message: errormessage }));
        ariaDiscribedBy = id + "-errormessage";
        ariaInValid = true;
    }
    else if (isValidDate && helptext) {
        HelpTextComponent = (React.createElement(HelpText_1.HelpText, { id: id }, helptext));
        ariaDiscribedBy = id + "-helptext";
    }
    if (!hideIcon && !displayInline) {
        IconComponent = (React.createElement(Icon_1.Icon, { "aria-label": "display calendar", id: id + "-button", onClick: onIconClick, symbol: "controlCalendar24", tabIndex: displayOnIconClickOnly ? '0' : '-1' }));
    }
    else if (!hideIcon && displayInline) {
        IconComponent = (React.createElement(Icon_1.Icon, { "aria-label": "display calendar", symbol: "controlCalendar24", tabIndex: "-1", type: "svgfile" }));
    }
    return (React.createElement("div", { className: "date-picker " + className, id: id, ref: modRef },
        React.createElement("div", { className: "form-group" },
            React.createElement("span", { className: "sr-only" }, screenText),
            React.createElement("label", { htmlFor: id + "-input" }, label),
            React.createElement("div", { className: "input-holder" },
                React.createElement("input", { ref: !isValidDate ? ref : undefined, id: id + "-input", className: (0, classnames_1.default)('form-control', {
                        'is-invalid': !isValidDate && !isInputSelectedByCalendar,
                    }), name: name + "StartDate", type: "text", inputMode: "numeric", value: inputDate, onFocus: onInputFocus, onBlur: onInputBlur, onChange: onInputChange, "aria-describedby": ariaDiscribedBy, "aria-invalid": ariaInValid }),
                IconComponent),
            HelpTextComponent,
            ErrorMessageComponent),
        React.createElement("div", { ref: calRef, hidden: !showCalendar && !displayInline, className: (0, classnames_1.default)({
                'date-picker__absolute': !displayInline,
                'date-picker__absolute-top': !displayInline && !displayCalBottom,
                'date-picker__inline': displayInline,
            }) },
            React.createElement("div", { className: "date-picker__month" },
                React.createElement(ButtonAction_1.ButtonActionSubtle, { className: "date-picker__month__btn", id: id + "-prev-month-btn", onClick: onPreviousMonthBtnClick, "aria-label": "Previous month" },
                    React.createElement(Icon_1.Icon, { symbol: "controlChevronForward24", type: "svgfile", className: "icon-rotated" })),
                React.createElement("span", { className: "date-picker__month__text" }, (0, date_fns_1.format)(calendarMonthYear, 'MMM yyyy')),
                React.createElement(ButtonAction_1.ButtonActionSubtle, { className: "date-picker__month__btn", id: id + "-next-month-btn", onClick: onNextMonthBtnClick, "aria-label": "Next month" },
                    React.createElement(Icon_1.Icon, { symbol: "controlChevronForward24", type: "svgfile" }))),
            React.createElement("div", { className: "mt-1 w-100 calendar-container", id: id + "-calendar" },
                React.createElement(Calendar_1.Calendar, { id: id + "-single-calendar", isActive: calendarFocus, ignoreDates: ignoreDates, ignoreWeekends: ignoreWeekends, month: (0, date_fns_1.getMonth)(calendarMonthYear), year: (0, date_fns_1.getYear)(calendarMonthYear), onClick: onCalendarDayClick, dateFormat: dateFormat, displayOnHover: !displayInline, startDate: selectedDate, className: "m-auto m-md-0", maximumDate: maximumDate, minimumDate: minimumDate, caption: (0, date_fns_1.format)(calendarMonthYear, 'MMM yyyy'), onChangeCalendarMonth: changeMonthDisplay, onChangeCalendarStatus: switchCalendarFocus, inFocusChange: calendarState, startFocusDate: focusDate, smallScreenSize: smallScreenSize })),
            calendarChildren && calendarChildren)));
});
exports.SingleDatePicker.propTypes = {
    calendarChildren: PropTypes.any,
    className: PropTypes.string,
    dateFormat: PropTypes.string,
    displayInline: PropTypes.bool,
    displayOnIconClickOnly: PropTypes.bool,
    errormessage: PropTypes.string,
    hideIcon: PropTypes.bool,
    id: PropTypes.string.isRequired,
    ignoreDates: PropTypes.array,
    ignoreWeekends: PropTypes.bool,
    initialCalendarMonth: PropTypes.instanceOf(Date),
    isValidDate: PropTypes.bool,
    label: PropTypes.string.isRequired,
    maximumDate: PropTypes.instanceOf(Date),
    minimumDate: PropTypes.instanceOf(Date),
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    screenReaderText: PropTypes.string,
    value: PropTypes.string,
};
exports.default = exports.SingleDatePicker;
